import { ContractDtoBidBasisEnum } from '@b-stock/contract-api-client'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { ListingDtoListingTypeEnum } from '@b-stock/listing-api-client'

import ContractMaoTaxBanner from './ContractMaoTaxBanner'

/**
 * ContractMaoTaxBannerContainer — Contract / MAO deferred-tax banner
 * wiring for the listing detail page (T094 / FR-010 of the
 * Canadian-tax spec).
 *
 * Renders `<ContractMaoTaxBanner />` only when the listing is a
 * Contract / MAO listing on a CAD storefront — anywhere else the
 * banner has no business showing:
 *
 *   - `listingType === 'CONTRACT'` AND `contract.bidBasis === 'RELATIVE'`
 *     — only %-of-MSRP / %-of-COGS bidding has no knowable won-at
 *     amount at bid-time. A CONTRACT listing with ABSOLUTE bidBasis
 *     still resolves a concrete bid amount, so the standard running
 *     estimate / max-exposure UI applies (US2 path) and the deferred
 *     banner would be misleading there.
 *
 *   - a tax regime actually applies — `CAD` (Canadian tax), `EUR`
 *     (EU VAT, FR-013), or `USD` shipping from a Canadian warehouse
 *     (FP-3012). Mirrors the gate used by the US2 containers.
 *
 * Pair half: `RunningTaxEstimateContainer` is updated in the same MR
 * to suppress the running estimate on Contract / MAO listings (T093
 * suppression). The MaxTaxExposure suppression was already in place
 * via `BidConfirmationModal`'s `!isContract` gate (T081 / FR-009).
 */

/**
 * Which deferred-tax copy a currency gets, and by omission which
 * currencies get no banner at all.
 *
 * Deliberately a allow-list rather than a "not USD" check: the banner
 * promises that tax WILL be calculated later, so showing it anywhere
 * the backend cannot actually produce a figure would be a false
 * promise. `EUR` is the only EU currency the tax service's calc
 * endpoint accepts — an EU order denominated in DKK / SEK / PLN / CZK
 * resolves no rate and surfaces the unavailable state instead, so it
 * must not get this banner either.
 */
const CANADIAN_DEFERRED_MESSAGE = 'ContractMaoTaxBanner.deferredTax'

const DEFERRED_MESSAGE_BY_CURRENCY: Record<string, string | undefined> = {
  CAD: CANADIAN_DEFERRED_MESSAGE,
  EUR: 'ContractMaoTaxBanner.deferredVat',
}

/**
 * FP-3012 — a Canadian supply is taxed in USD too, so USD Contract/MAO
 * listings defer tax the same way CAD ones do.
 *
 * USD cannot join the allow-list above, though: unlike CAD and EUR the
 * denomination says nothing about the regime, and most USD listings are
 * US-domestic where nothing is calculated later. Key off the warehouse
 * instead — a non-Canadian one can never produce a Canadian figure, so it
 * keeps getting no banner. A Canadian warehouse shipping out of the
 * country is a 0% export, which the CAD path already promises tax on;
 * this is deliberately no wider than that.
 */
const resolveDeferredMessage = (
  listing: OrchestratedListingDetails
): string | undefined => {
  // Normalised for the same reason isCanadianTaxCurrency normalises:
  // listing.currency is not guaranteed upper-case.
  const currency = listing.currency?.toUpperCase()
  const byCurrency = currency
    ? DEFERRED_MESSAGE_BY_CURRENCY[currency]
    : undefined
  if (byCurrency) {
    return byCurrency
  }
  return currency === 'USD' && listing.lot.location.countryCode === 'CA'
    ? CANADIAN_DEFERRED_MESSAGE
    : undefined
}

const ContractMaoTaxBannerContainer = ({
  listing,
}: {
  listing: OrchestratedListingDetails
}) => {
  const isContractMao =
    listing.listingType === ListingDtoListingTypeEnum.Contract &&
    listing.contract?.bidBasis === ContractDtoBidBasisEnum.Relative
  const messageId = resolveDeferredMessage(listing)

  if (!isContractMao || !messageId) {
    return null
  }

  return <ContractMaoTaxBanner messageId={messageId} />
}

export default ContractMaoTaxBannerContainer
